import { useState } from 'react';
import { LoaderCircle, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { api } from '@/lib/api';

function MicrosoftIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

export function LoginPage() {
  const { login, authError } = useAuth();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleSSOSignIn = async () => {
    setError('');
    setSubmitting(true);
    try {
      await login();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start sign in');
      setSubmitting(false);
    }
    // Don't setSubmitting(false) on success — browser navigates to Entra ID
  };

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      // Check if the email's domain requires SSO (a per-domain answer only —
      // A2F-2547: the backend never reveals whether this email is registered)
      const { authMethod } = await api.auth.checkDomain(trimmed);

      if (authMethod === 'sso') {
        // Domain requires SSO — redirect to Microsoft sign-in
        // (SSO users may not be in org_users yet; they're provisioned on first login)
        await login();
        return; // Browser navigates away
      }

      // For email/magic link: always request the link. shouldCreateUser:false
      // means Supabase only mails existing accounts; who may actually enter the
      // app is enforced server-side after login. ALWAYS show the same "check
      // your email" screen regardless of outcome to prevent email enumeration.
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: false,
        },
      });

      if (otpError) {
        // Suppress OTP errors for anti-enumeration — Supabase returns
        // "Signups not allowed" when shouldCreateUser is false and the
        // email doesn't exist in Supabase Auth.
        console.warn('OTP error (suppressed for anti-enumeration):', otpError.message);
      }

      // Always show "check your email" — identical UX for allowed and disallowed emails
      setMagicLinkSent(true);
      setSubmitting(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/chrona-logo.png"
            alt="Chrona"
            className="w-16 h-16 mx-auto mb-3 object-contain"
          />
          <h1 className="text-xl font-bold text-brand-charcoal tracking-wide">CHRONA</h1>
          <p className="text-xs text-brand-orange font-semibold tracking-[0.15em] uppercase mt-0.5">
            Timely Biotech AI
          </p>
        </div>

        <h2 className="text-lg font-bold text-brand-charcoal text-center mb-6">Sign In</h2>

        {(authError || error) && (
          <div className="px-4 py-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {authError || error}
          </div>
        )}

        {magicLinkSent ? (
          <div className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 bg-green-50 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-brand-charcoal mb-1">Check your email</p>
            <p className="text-xs text-slate-500 mb-4">
              If this email is associated with an account, you'll receive a sign-in link.
            </p>
            <button
              onClick={() => { setMagicLinkSent(false); setEmail(''); }}
              className="text-xs text-brand-orange hover:underline"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            {/* Primary: Microsoft SSO */}
            <button
              onClick={handleSSOSignIn}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-slate-300 rounded-lg text-sm font-semibold text-brand-charcoal bg-white hover:bg-slate-50 transition-colors disabled:opacity-40 shadow-sm"
            >
              {submitting && !showEmailForm ? (
                <LoaderCircle className="w-5 h-5 animate-spin" />
              ) : (
                <MicrosoftIcon />
              )}
              Sign in with Microsoft
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Secondary: Email sign-in (collapsed by default) */}
            {!showEmailForm ? (
              <button
                onClick={() => setShowEmailForm(true)}
                className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-brand-charcoal transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                Sign in with email
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            ) : (
              <div>
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-brand-charcoal transition-colors mb-3"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Sign in with email
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <form onSubmit={handleEmailContinue}>
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm text-brand-charcoal placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange disabled:opacity-40 mb-3"
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={submitting || !email.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-brand-orange/90 transition-colors disabled:opacity-40"
                  >
                    {submitting ? (
                      <LoaderCircle className="w-4 h-4 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4" />
                    )}
                    Send sign-in link
                  </button>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
